import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { ActionBackOfficeResponse } from '../../../../../../core/models/api.model';
import { ParkingGateListResponse, ParkingTransactionResponse } from '../../../../../../core/models/parking.model';
import { ForceExitTransactionPayload } from '../../../../../../core/models/parking/transaction-parking.model';
import { ApiService } from '../../../../../../core/services/api.service';
import { ApiTransactionParkingService } from '../../../../../../core/services/api/parking/api-transaction-parking.service';
import { MenuPermissionService } from '../../../../../../core/services/menu-permission.service';
import { PssConfirmModalComponent } from '../../../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-modal-force-exit-transaction',
  standalone: true,
  imports: [CommonModule, FormsModule, PssConfirmModalComponent, PssLoadingComponent],
  templateUrl: './modal-force-exit-transaction.component.html',
  styleUrl: './modal-force-exit-transaction.component.css',
})
export class ModalForceExitTransactionComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() siteId = 0;
  @Input() userId = 0;
  @Input() transaction: ParkingTransactionResponse | null = null;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSuccess = new EventEmitter<void>();

  private readonly apiTx = inject(ApiTransactionParkingService);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  parkingGateId = 0;
  reason = '';
  openGate = false;
  isSubmitting = false;
  showConfirm = false;
  confirmMessage = '';

  exitGates: ParkingGateListResponse[] = [];
  gateSearchText = '';
  isGateDropdownOpen = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetForm();
      this.loadGates();
    }
  }

  get filteredGates(): ParkingGateListResponse[] {
    const q = this.gateSearchText.trim().toLowerCase();
    if (!q) return this.exitGates;
    return this.exitGates.filter((g) => g.name.toLowerCase().includes(q));
  }

  get selectedGateName(): string {
    return this.exitGates.find((g) => g.id === this.parkingGateId)?.name ?? '';
  }

  private resetForm(): void {
    this.parkingGateId = 0;
    this.reason = '';
    this.openGate = false;
    this.isSubmitting = false;
    this.showConfirm = false;
    this.confirmMessage = '';
    this.gateSearchText = '';
    this.isGateDropdownOpen = false;
    this.localOnly = false;
  }

  private loadGates(): void {
    const siteId = this.siteId > 0 ? this.siteId : (this.transaction?.siteId ?? 0);
    if (siteId <= 0) return;

    this.apiService.getParkingGate(siteId).subscribe({
      next: (res) => {
        this.exitGates = (res ?? []).filter((g) => this.isExitGate(g));
      },
      error: () => (this.exitGates = []),
    });
  }

  selectGate(gate: ParkingGateListResponse): void {
    this.parkingGateId = gate.id;
    this.gateSearchText = gate.name;
    this.isGateDropdownOpen = false;
  }

  closeDropdowns(): void {
    this.isGateDropdownOpen = false;
    if (this.parkingGateId > 0) {
      this.gateSearchText = this.selectedGateName;
    }
  }

  close(): void {
    if (this.isSubmitting) return;
    this.showConfirm = false;
    this.onClose.emit();
  }

  /** ประตูออก — parking_gate_type.exit ต้องเป็น true */
  private isExitGate(g: ParkingGateListResponse): boolean {
    const raw = g as ParkingGateListResponse & { Exit?: boolean };
    return g.exit === true || raw.Exit === true;
  }

  askSubmit(): void {
    if (this.isSubmitting) return;

    if (!this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์ทำรายการออก');
      return;
    }

    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId) {
      this.toast.error('ไม่พบ Menu ID');
      return;
    }

    if (this.localOnly) {
      if (!this.transaction?.id || this.userId <= 0) {
        this.toast.error('ไม่พบรายการหรือผู้ใช้งาน');
        return;
      }
      if (!this.reason.trim()) {
        this.toast.error('กรุณาระบุเหตุผล');
        return;
      }
      this.confirmMessage = `ทะเบียน ${this.transaction?.entryLicense || '-'} | ปิดเฉพาะใน DB (ไม่ส่ง Taiwan)`;
      this.showConfirm = true;
      return;
    }

    const cardCode = (this.transaction?.cardCode ?? '').trim();
    const siteId = this.siteId > 0 ? this.siteId : (this.transaction?.siteId ?? 0);
    if (!cardCode) {
      this.toast.error('ไม่พบ card code ของรายการนี้');
      return;
    }
    if (siteId <= 0 || this.userId <= 0) {
      this.toast.error('กรุณาเลือกลานและเข้าสู่ระบบให้ถูกต้อง');
      return;
    }
    if (this.parkingGateId <= 0) {
      this.toast.error('กรุณาเลือกประตูออก');
      return;
    }
    const reason = this.reason.trim();
    if (!reason) {
      this.toast.error('กรุณาระบุเหตุผล');
      return;
    }

    const plate = this.transaction?.entryLicense || '-';
    this.confirmMessage =
      `ทะเบียน ${plate} | ประตู ${this.selectedGateName || '-'}` +
      (this.openGate ? ' | เปิดไม้กั้น' : '');
    this.showConfirm = true;
  }

  closeConfirm(): void {
    if (this.isSubmitting) return;
    this.showConfirm = false;
  }

  confirmSubmit(): void {
    if (this.isSubmitting) return;
    this.showConfirm = false;
    this.submit();
  }

  private submit(): void {
    if (this.isSubmitting) return;
    if (this.localOnly) {
      this.submitLocalOnly();
      return;
    }

    const menuId = this.permissionService.getMenuId(this.router.url);
    const cardCode = (this.transaction?.cardCode ?? '').trim();
    const siteId = this.siteId > 0 ? this.siteId : (this.transaction?.siteId ?? 0);
    if (!menuId || !cardCode || siteId <= 0) return;

    const payload: ForceExitTransactionPayload = {
      userId: this.userId,
      menuId,
      siteId,
      cardCode,
      openGate: this.openGate,
      parkingGateId: this.parkingGateId,
      reason: this.reason.trim(),
    };

    this.isSubmitting = true;
    this.apiTx.forceExitTransaction(payload).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        console.log('forceExitTransaction', res);
        const raw = res as ActionBackOfficeResponse & Record<string, unknown>;
        const ok = Boolean(res?.isSuccess ?? raw['IsSuccess']);
        const message = String(res?.message ?? raw['Message'] ?? '');
        if (ok) {
          this.toast.success(message || 'ทำรายการออกสำเร็จ');
          this.onSuccess.emit();
          this.close();
        } else {
          this.toast.error(message || 'ทำรายการออกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.toast.error(err?.error?.message || err?.error || 'ทำรายการออกไม่สำเร็จ');
      },
    });
  }

  // --- ปิดเฉพาะใน DB (ไม่ส่ง Taiwan) — กรณี Taiwan ปิดแล้วแต่ไม่แจ้งกลับมา ---
  localOnly = false;

  private submitLocalOnly(): void {
    const menuId = this.permissionService.getMenuId(this.router.url);
    const txId = this.transaction?.id ?? 0;
    if (!menuId || txId <= 0) return;

    this.isSubmitting = true;
    this.apiService
      .closingTransaction(
        { userId: this.userId, remark: this.reason.trim(), menuId, parkingGateId: 0, localOnly: true },
        txId,
      )
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res?.isSuccess) {
            this.toast.success(res?.message || 'ปิดรายการใน DB สำเร็จ');
            this.onSuccess.emit();
            this.close();
          } else {
            this.toast.error(res?.message || 'ปิดรายการไม่สำเร็จ');
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.toast.error(err?.error?.message || err?.error || 'ปิดรายการไม่สำเร็จ');
        },
      });
  }
}
